import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, LockClosedIcon, EyeIcon, ServerIcon, UserIcon } from '@heroicons/react/24/outline';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-900 to-dark-950">
      <div className="relative">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        
        <div className="relative mx-auto max-w-4xl px-6 py-24 sm:py-32 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="flex justify-center mb-6">
              <div className="p-3 rounded-full bg-primary-500/20">
                <ShieldCheckIcon className="h-8 w-8 text-primary-400" />
              </div>
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-6xl">
              Privacy Policy
            </h1>
            <p className="mt-6 text-lg leading-8 text-primary-200">
              Your privacy is our priority. Learn how Speedr protects your data.
            </p>
            <p className="mt-2 text-sm text-primary-400">
              Last Updated: March 1, 2025
            </p>
          </motion.div>

          <div className="space-y-8">
            {/* Introduction */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-8 border border-primary-900/20"
            >
              <h2 className="text-2xl font-bold text-white mb-4">Introduction</h2>
              <p className="text-primary-200 leading-relaxed">
                This Privacy Policy (hereinafter referred to as the "Policy") is provided by SpeedR Inc. (hereinafter referred to as the "Company") and governs the collection, use, processing, and protection of personal information in connection with your use of the AI Assistant application (hereinafter referred to as the "Application" or "App"). Users are required to review and acknowledge this Policy carefully, as continued use of the Application constitutes acceptance of the terms herein.
              </p>
            </motion.div>

            {/* Data Collection Policy */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-8 border border-primary-900/20"
            >
              <div className="flex items-center mb-4">
                <div className="p-2 rounded-lg bg-primary-500/20 mr-3">
                  <EyeIcon className="h-6 w-6 text-primary-400" />
                </div>
                <h2 className="text-2xl font-bold text-white">1. Data Collection Policy</h2>
              </div>
              
              <p className="text-primary-200 leading-relaxed">
                The Company does not collect, store, transmit, or process any user data. The Application operates exclusively in an offline environment on the user's device. No personal information, usage data, conversational content, or metadata is gathered, stored, or transmitted to the Company's servers, third-party entities, or any external systems.
              </p>
            </motion.div>

            {/* Offline Processing */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-8 border border-primary-900/20"
            >
              <div className="flex items-center mb-4">
                <div className="p-2 rounded-lg bg-primary-500/20 mr-3">
                  <ServerIcon className="h-6 w-6 text-primary-400" />
                </div>
                <h2 className="text-2xl font-bold text-white">2. Offline Processing</h2>
              </div>
              
              <p className="text-primary-200 leading-relaxed">
                All data processing activities, including interactions with the AI assistant, are conducted solely on the user's device. User interactions, inputs, and outputs remain confined to the device and are not transmitted to any external servers, third parties, or the Company. This offline operation ensures the utmost privacy and security of user data.
              </p>
            </motion.div>

            {/* Account Information */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-8 border border-primary-900/20"
            >
              <h2 className="text-2xl font-bold text-white mb-4">3. Account Information</h2>
              <p className="text-primary-200 leading-relaxed">
                If the creation of a user account is required to facilitate the purchase or authentication of the Application, the Company will collect only the minimum information strictly necessary for these purposes, including verification of the user's purchase and provision of access to the Application. This information will not be used for any other commercial, analytical, or operational purposes by the Company or its affiliates.
              </p>
            </motion.div>

            {/* Authentication Information */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-8 border border-primary-900/20"
            >
              <h2 className="text-2xl font-bold text-white mb-4">4. Authentication Information</h2>
              <p className="text-primary-200 leading-relaxed">
                When users employ third-party authentication methods (e.g., Google, Apple, or email) to access the Application, authentication is processed through Firebase Authentication or similar providers. The Company does not store, access, or retain any user login credentials or authentication data. Any data collected by these third-party providers is governed by their respective privacy policies, for which the Company bears no responsibility.
              </p>
            </motion.div>

            {/* Local Storage */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-8 border border-primary-900/20"
            >
              <h2 className="text-2xl font-bold text-white mb-4">5. Local Storage</h2>
              <p className="text-primary-200 leading-relaxed">
                The Application may store user preferences, conversation history, and related data locally on the user's device to enhance functionality and user experience. This data remains confined to the device and will not be transmitted, shared, or accessed by any external entities, including the Company, without explicit user consent.
              </p>
            </motion.div>

            {/* No Data Sharing */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-8 border border-primary-900/20"
            >
              <div className="flex items-center mb-4">
                <div className="p-2 rounded-lg bg-primary-500/20 mr-3">
                  <LockClosedIcon className="h-6 w-6 text-primary-400" />
                </div>
                <h2 className="text-2xl font-bold text-white">6. No Data Sharing</h2>
              </div>
              
              <p className="text-primary-200 leading-relaxed">
                Since the Company does not collect or process user data, no user information, personal or otherwise, will be shared, sold, transferred, disclosed, or made available to third parties, affiliates, or external systems. Users' privacy rights are fully protected under this Policy.
              </p>
            </motion.div>

            {/* Children's Privacy */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-8 border border-primary-900/20"
            >
              <h2 className="text-2xl font-bold text-white mb-4">7. Children's Privacy</h2>
              <p className="text-primary-200 leading-relaxed">
                The Application is not intended for, directed at, or marketed to individuals under the age of 13. The Company does not knowingly collect, store, or process any personal information from children under 13 years of age. If the Company becomes aware of such data collection, it will be promptly deleted in accordance with applicable law.
              </p>
            </motion.div>

            {/* Security Measures */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.0 }}
              className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-8 border border-primary-900/20"
            >
              <h2 className="text-2xl font-bold text-white mb-4">8. Security Measures</h2>
              <p className="text-primary-200 leading-relaxed">
                Given that all data processing occurs locally on the user's device, user data is inherently secured against unauthorized access or transmission. The Company recommends that users implement robust security measures on their devices, including but not limited to strong passwords, PINs, biometric authentication (e.g., fingerprint or facial recognition), and other appropriate safeguards to protect their data.
              </p>
            </motion.div>

            {/* Amendments */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.1 }}
              className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-8 border border-primary-900/20"
            >
              <h2 className="text-2xl font-bold text-white mb-4">9. Amendments to Privacy Policy</h2>
              <p className="text-primary-200 leading-relaxed">
                The Company reserves the right to modify, amend, or update this Privacy Policy at its sole discretion and at any time. Notice of material changes shall be provided to users by posting the revised Policy on the Company's website or within the Application and updating the "Last Updated" date. Continued use of the Application following such modifications constitutes the user's affirmative acceptance of the revised Policy.
              </p>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.2 }}
              className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-8 border border-primary-900/20"
            >
              <div className="flex items-center mb-4">
                <div className="p-2 rounded-lg bg-primary-500/20 mr-3">
                  <UserIcon className="h-6 w-6 text-primary-400" />
                </div>
                <h2 className="text-2xl font-bold text-white">10. Contact Information</h2>
              </div>
              
              <p className="text-primary-200 leading-relaxed mb-4">
                For any inquiries, concerns, or requests related to this Privacy Policy, users may contact the Company at:
              </p>
              
              <div className="bg-dark-900/50 rounded-lg p-6 border border-primary-900/30">
                <p className="text-primary-200">
                  <strong>Email:</strong> customer-support@speedr.co
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
